// src/components/Watchlist.js
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  X, 
  Eye, 
  Bell,
  TrendingUp,
  TrendingDown,
  Star,
  Search
} from 'lucide-react';
import { useWebSocket } from '../../hooks/useWebSocket';

const Watchlist = ({ darkMode, portfolio }) => {
  const [watchlist, setWatchlist] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [newSymbol, setNewSymbol] = useState('');
  const { realTimePrices, subscribeToSymbol, unsubscribeFromSymbol } = useWebSocket();

  // Initialize with portfolio symbols
  useEffect(() => {
    const initialWatchlist = portfolio.map(stock => ({
      symbol: stock.symbol,
      name: stock.name,
      isWatching: true
    }));
    setWatchlist(initialWatchlist);
  }, [portfolio]);

  const addToWatchlist = (symbol) => {
    if (symbol && !watchlist.find(item => item.symbol === symbol.toUpperCase())) {
      const newItem = {
        symbol: symbol.toUpperCase(),
        name: `Unknown (${symbol.toUpperCase()})`,
        isWatching: true
      };
      setWatchlist(prev => [...prev, newItem]);
      subscribeToSymbol(symbol.toUpperCase());
      setNewSymbol('');
    }
  };

  const removeFromWatchlist = (symbol) => {
    setWatchlist(prev => prev.filter(item => item.symbol !== symbol));
    unsubscribeFromSymbol(symbol);
  };

  const toggleWatching = (symbol) => {
    setWatchlist(prev => prev.map(item =>
      item.symbol === symbol 
        ? { ...item, isWatching: !item.isWatching }
        : item
    ));
  };

  const filteredWatchlist = watchlist.filter(item =>
    item.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPriceChange = (symbol) => {
    const price = realTimePrices[symbol] || 100;
    const change = (Math.random() - 0.5) * 10; // Mock change
    return { price, change };
  };

  const handleButtonClick = async (action, symbol, event) => {
    try {
      event.preventDefault();
      event.stopPropagation();
      
      switch (action) {
        case 'toggleWatching':
          toggleWatching(symbol);
          break;
        case 'removeFromWatchlist':
          removeFromWatchlist(symbol);
          break;
        // case 'setAlert':
        //   await handleSetAlert(symbol, event);
        //   break;
        case 'addSymbol':
          addToWatchlist(newSymbol);
          break;
        default:
          console.warn('Unknown action:', action);
      }
    } catch (error) {
      console.error('Error in watchlist button click:', error);
    }
  };  

  return (
    <div className={`rounded-lg shadow-lg p-6 ${
      darkMode ? 'bg-gray-800' : 'bg-white'
    }`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center">
          <Eye size={24} className="mr-3 text-purple-600" />
          <h3 className="text-2xl font-bold">Watchlist</h3>
          <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
            {watchlist.length} symbols
          </span>
        </div>

        {/* Add Symbol Form */}
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search watchlist..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 pr-4 py-2 rounded-lg border text-sm ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="text"
              placeholder="Add symbol (e.g., AAPL)"
              value={newSymbol}
              onChange={(e) => setNewSymbol(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addToWatchlist(newSymbol)}
              className={`px-4 py-2 rounded-lg border text-sm ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
            <button
              onClick={() => addToWatchlist(newSymbol)}
              className={`p-2 rounded-lg ${
                darkMode 
                  ? 'bg-green-700 text-white hover:bg-green-600' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Watchlist Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Symbol
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Change
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredWatchlist.map((item) => {
              const { price, change } = getPriceChange(item.symbol);
              const isPositive = change >= 0;

              return (
                <tr key={item.symbol} className={
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Star className={`h-4 w-4 mr-2 ${
                        item.isWatching ? 'text-yellow-400' : 'text-gray-400'
                      }`} />
                      <span className="font-bold">{item.symbol}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">{item.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold">
                      ${price.toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`flex items-center text-sm font-bold ${
                      isPositive 
                        ? darkMode ? 'text-green-400' : 'text-green-600'
                        : darkMode ? 'text-red-400' : 'text-red-600'
                    }`}>
                      {isPositive ? (
                        <TrendingUp size={14} className="mr-1" />
                      ) : (
                        <TrendingDown size={14} className="mr-1" />
                      )}
                      {isPositive ? '+' : ''}{change.toFixed(2)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      item.isWatching
                        ? darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-800'
                        : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {item.isWatching ? 'Watching' : 'Paused'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => handleButtonClick('toggleWatching', item.symbol, e)}
                        className={`p-1 rounded ${
                          item.isWatching
                            ? darkMode ? 'bg-yellow-600 text-white' : 'bg-yellow-500 text-white'
                            : darkMode ? 'bg-gray-600 text-white' : 'bg-gray-500 text-white'
                        }`}
                        title={item.isWatching ? 'Pause watching' : 'Resume watching'}
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={(e) => handleButtonClick('removeFromWatchlist', item.symbol, e)}
                        className={`p-1 rounded ${
                          darkMode ? 'bg-red-600 text-white' : 'bg-red-500 text-white'
                        }`}
                        title="Remove from watchlist"
                      >
                        <X size={14} />
                      </button>
                      <button
//                        onClick={(e) => handleButtonClick('setAlert', item.symbol, e)}
                        className={`p-1 rounded ${
                          darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                        }`}
                        title="Set price alert"
                      >
                        <Bell size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredWatchlist.length === 0 && (
        <div className={`text-center py-12 ${
          darkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <Eye size={48} className="mx-auto mb-4 opacity-50" />
          <p>No symbols in watchlist. Add some symbols to track.</p>
        </div>
      )}

      {/* Quick Actions */}
      <div className={`mt-6 p-4 rounded-lg ${
        darkMode ? 'bg-gray-700' : 'bg-gray-50'
      }`}>
        <h4 className="font-semibold mb-3">Quick Actions</h4>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setWatchlist(prev => prev.map(item => ({
              ...item,
              isWatching: true
            })))}
            className={`px-3 py-2 rounded text-sm ${
              darkMode 
                ? 'bg-green-700 text-white hover:bg-green-600' 
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            Watch All
          </button>
          <button
            onClick={() => setWatchlist(prev => prev.map(item => ({
              ...item,
              isWatching: false
            })))}
            className={`px-3 py-2 rounded text-sm ${
              darkMode 
                ? 'bg-yellow-700 text-white hover:bg-yellow-600' 
                : 'bg-yellow-600 text-white hover:bg-yellow-700'
            }`}
          >
            Pause All
          </button>
          <button
            onClick={() => {
              watchlist.forEach(item => unsubscribeFromSymbol(item.symbol));
              setWatchlist([]);
            }}
            className={`px-3 py-2 rounded text-sm ${
              darkMode 
                ? 'bg-red-700 text-white hover:bg-red-600' 
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            Clear All
          </button>
        </div>
      </div>
    </div>
  );
};

export default Watchlist;